// One wrapper for every backend call.
//
// It attaches the token, turns an error response into a thrown ApiError so
// pages can use try/catch, and handles 401 in a single place. Without that last
// part every page would need its own expired-session branch, and one of them
// would get it wrong.

// Relative, because the app is served from the same origin as the API. There
// is no API host to configure, and therefore none to get wrong.
const BASE = "/api"

const TOKEN_KEY = "moderniser_token"
const COMPANY_KEY = "moderniser_company"

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
}

// Which company this browser acts for, sent as the x-moderniser-company
// header. Unset works for anyone in one company — the API acts for it. For
// someone in several the API answers 409 company_required, and the app picks
// the last company used here (lastCompanyStore) and keeps it in this one.
export const companyStore = {
  get: () => localStorage.getItem(COMPANY_KEY),
  set: (id: string) => localStorage.setItem(COMPANY_KEY, id),
  clear: () => localStorage.removeItem(COMPANY_KEY),
}

// The company this browser last worked in. Unlike companyStore it survives
// sign-out: it is where someone in several companies lands next time, instead
// of being asked. Only a preference — the API checks membership on every
// request, so a stale or someone else's value just falls back to the first.
const LAST_COMPANY_KEY = "moderniser_last_company"
export const lastCompanyStore = {
  get: () => localStorage.getItem(LAST_COMPANY_KEY),
  set: (id: string) => localStorage.setItem(LAST_COMPANY_KEY, id),
}

// Set by the auth store so a 401 anywhere can drop the session without this
// module importing React or the router.
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = tokenStore.get()
  const company = companyStore.get()

  // Don't set Content-Type for FormData (browser will set it with boundary)
  const isFormData = init.body instanceof FormData
  const headers: Record<string, string> = {}
  if (token) headers["Authorization"] = `Bearer ${token}`
  if (company) headers["x-moderniser-company"] = company
  if (!isFormData) headers["Content-Type"] = "application/json"
  Object.assign(headers, init.headers)

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers,
    })
  } catch {
    // A network failure and a 500 are different things to a user: one is worth
    // retrying immediately, the other is not.
    throw new ApiError(0, "Could not reach the server. Check your connection and try again.")
  }

  if (res.status === 401) {
    tokenStore.clear()
    onUnauthorized?.()
    throw new ApiError(401, "Your session has expired. Sign in again.")
  }

  if (res.status === 204) return undefined as T

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    // The API's own message is nearly always the useful one — it says which
    // field was wrong, not merely that something was.
    throw new ApiError(res.status, (body.error as string) ?? `Something went wrong (${res.status})`)
  }
  return body as T
}

/** Per-request options. `company` acts for that company for this one request
 *  instead of the switcher's — the timesheet edits an entry in whichever of
 *  your companies it belongs to. The API still checks the membership. */
export type RequestOptions = { company?: string }

const withCompany = (options?: RequestOptions): RequestInit =>
  options?.company ? { headers: { "x-moderniser-company": options.company } } : {}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, withCompany(options)),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...withCompany(options), method: "POST", body: JSON.stringify(body ?? {}) }),
  postMultipart: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", body: formData }),
  patchMultipart: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "PATCH", body: formData }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown, options?: RequestOptions) =>
    request<T>(path, { ...withCompany(options), method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...withCompany(options), method: "DELETE" }),
  /** For endpoints that answer with something other than JSON, like the CSV
   *  export. Goes through the same auth header as everything else — a plain
   *  <a href> would be an unauthenticated navigation. */
  raw: async (path: string) => {
    const token = tokenStore.get()
    const company = companyStore.get()
    const res = await fetch(`${BASE}${path}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(company && { "x-moderniser-company": company }),
      },
    })
    if (!res.ok) throw new ApiError(res.status, await res.text())
    return res.text()
  },
}

// ---------- shapes the API returns ----------

// The roles held per company. Mirrors CompanyRole in src/auth/validate.ts,
// which web/ cannot import — change both together.
export type CompanyRole = "admin" | "developer" | "external"

export type Employee = {
  id: string
  name: string
  email: string
  role: CompanyRole | "platform_admin"
  status: string
  /** The company's own switch, separate from the account status above. */
  membershipStatus: "active" | "disabled"
  /** No password yet — an imported account nobody has set up. */
  neverSetUp: boolean
  spendCap24hMicroUsd: number | null
  spendCap7dMicroUsd: number | null
  spendCap30dMicroUsd: number | null
  createdAt: string
}

export type Company = {
  id: string
  name: string
  domain: string
  creditsMicroUsd: number
  creditsFormatted: string
  createdAt?: string
}

export type Project = {
  id: string
  name: string
  description: string | null
  /** Three uppercase letters that task keys are built from, e.g. "CAD". */
  keyPrefix: string | null
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  /** Open tasks per status id. Archived tasks are not counted. */
  counts: Record<string, number>
  /** The effective board configuration — always present on list and detail. */
  config: BoardConfig
}

// Boards are configurable per project (src/projects/board-config.ts), so
// status and type are open strings whose meaning lives in the project's
// config. Category is what charts and colours key off — it is the part that
// cannot be customised.
export type Status = string
export type TaskType = string
export type StatusCategory = "todo" | "in_progress" | "done"
export type BoardColor =
  | "gray" | "blue" | "cyan" | "green" | "yellow" | "orange" | "red" | "purple" | "pink"

export type BoardConfig = {
  statuses: { id: string; name: string; category: StatusCategory; color: BoardColor }[]
  types: { id: string; name: string; color: BoardColor }[]
  /** Most urgent first; the order pickers list them in. */
  priorities: { id: string; name: string; color: BoardColor }[]
  /** What a new task gets when its creator picks none. */
  defaultPriority: string
  labels: { name: string; color: BoardColor }[]
  fields: { id: string; name: string; type: "text" | "number" | "date" | "select"; options?: string[] }[]
}

export type ProjectInsights = {
  summary: {
    open: number
    done: number
    byCategory: Record<string, number>
    overdue: number
    minutesLogged: number
    medianOpenAgeDays: number | null
  }
  progress: { day: string; created: number; completed: number }[]
  hours: {
    byDay: { day: string; minutes: number }[]
  }
  upcoming: {
    id: string
    key: string
    title: string
    status: string
    startDate: string | null
    dueDate: string
    assigneeId: string | null
  }[]
}

// ---------- Time Report ----------

export type TimeReportGroupBy =
  | "employee"
  | "company"
  | "project"
  | "sprint"
  | "epic"
  | "task"
  | "parent"
  | "label"
  | "day"
  | "week"
  | "month"

export type TimeReportFilters = {
  companyIds: string[]
  projectIds: string[]
  sprintIds: string[]
  epicIds: string[]
  taskIds: string[]
  /** The direct parent — what a Parent group holds. Narrower than taskIds,
   *  which also takes the task's own time and its grandchildren's. */
  parentIds: string[]
  labels: string[]
  employeeIds: string[]
  from: string | null
  to: string | null
}

export type TimeReportEntry = {
  id: string
  spentOn: string
  minutes: number
  note: string | null
  author: { id: string; name: string; email: string }
  /** The admin who logged it for the author; null when they logged it themselves. */
  loggedBy: { id: string; name: string | null } | null
  task: {
    id: string
    key: string
    title: string
    level: string
    status: string
    estimateMinutes: number | null
  }
  project: { id: string; name: string; companyName: string }
  epic: { key: string; title: string } | null
  sprint: string | null
  labels: string[]
}

export type TimeReportEntriesPage = {
  entries: TimeReportEntry[]
  totalMinutes: number
  summary: { entries: number; people: number; tasks: number }
  pagination: {
    page: number
    pageSize: number
    totalRows: number
    totalPages: number
  }
}

export type TimeReportGroupRow = {
  key: string
  label: string
  minutes: number
  entryCount: number
  estimateMinutes?: number | null
}

export type TimeReportGroups = {
  groupBy: TimeReportGroupBy
  rows: TimeReportGroupRow[]
  totalMinutes: number
  /** The rows' own sum across every page. Equals totalMinutes except for the
   *  label dimension, where an entry counts once per tag. */
  groupedMinutes: number
  pagination: {
    page: number
    pageSize: number
    totalRows: number
    totalPages: number
  }
}

export type TimeReportOptions = {
  companies: { id: string; name: string }[]
  /** Every company the viewer's report covers — for an admin, each company
   *  they are admin of — ordered by company, then project. */
  projects: { id: string; name: string; keyPrefix: string | null; companyId: string; companyName: string }[]
  sprints: { id: string; name: string; projectId: string }[]
  epics: { id: string; key: string; title: string; projectId: string }[]
  tasks: {
    id: string
    key: string
    title: string
    level: string
    projectId: string
    parentTaskId: string | null
  }[]
  labels: string[]
  employees: { id: string; name: string; email: string }[]
}

// ---------- Timesheets ----------

/** One card on the timesheet: the caller's own entry, with enough of its task
 *  to draw the key, the hover preview and the done-muting without a second
 *  request. `date` is the UTC calendar day the entry was logged for. */
export type TimesheetEntry = {
  id: string
  date: string
  minutes: number
  note: string | null
  createdAt: string
  task: {
    id: string
    key: string
    title: string
    level: TaskLevel
    status: Status
    /** The status's category is "done" on the task's own board. */
    done: boolean
    type: TaskType
    priority: Priority
    projectId: string
    projectName: string
    projectKeyPrefix: string | null
    /** The company the task belongs to — the sheet can span all of yours. */
    companyId: string
    companyName: string
    assigneeName: string | null
  }
}

/** What the Edit spent time dialog reads of an entry: a timesheet card is one
 *  as it stands, and a task page's row becomes one once its task is attached
 *  (lib/timesheet.ts editableEntry). */
export type EditableTimeEntry = Pick<TimesheetEntry, "id" | "date" | "minutes" | "note"> & {
  task: Pick<TimesheetEntry["task"], "id" | "key" | "title" | "companyId" | "companyName">
}

/** GET /timesheet/projects — a project the sheet's filter offers, from any of
 *  the companies the sheet covers, with the config its status names come from. */
export type TimesheetProject = {
  id: string
  name: string
  keyPrefix: string | null
  archivedAt: string | null
  companyId: string
  companyName: string
  config: BoardConfig
}

export type TimesheetResponse = {
  entries: TimesheetEntry[]
  totalMinutes: number
}

/** A priority id from the project's config. The five built-ins (urgent, high,
 *  medium, low, none) are only the defaults: an imported board brings its own. */
export type Priority = string

/** Where an item sits, as in YouTrack: an epic holds tasks, a task holds
 *  sub-tasks, a sub-task holds nothing. Separate from `type`, which is the
 *  board's own classification (Feature, Bug, …). */
export type TaskLevel = "epic" | "task" | "subtask"

/** The parent or epic of an item, as the single-task read returns them. */
export type TaskRef = { id: string; key: string; title: string; level: TaskLevel; archivedAt: string | null }

/** An image a task's description references by `attachment:<id>`, with the
 *  fresh signed URL the read hands out. Resolved at paint time — see
 *  lib/attachment-refs.ts for why the URL is never stored in the body. */
export type DescriptionAttachment = { id: string; url: string }

export type Task = {
  id: string
  projectId: string
  /** "CAD-42" — what a person quotes in a ticket. Unique within its project. */
  key: string
  title: string
  description: string
  type: TaskType
  level: TaskLevel
  status: Status
  /** "none" is what the agent writes: urgency is a business fact, not one it
   *  can read out of a repository. */
  priority: Priority
  labels: string[]
  startDate: string | null
  dueDate: string | null
  /** Values for the project's custom fields, keyed by field id. */
  customFields?: Record<string, string | number>
  /** Who last moved the status, and when. Null until it is first moved. */
  statusChangedBy: string | null
  statusChangedAt: string | null
  assigneeId: string | null
  /** The assignee's name, resolved on the server so a disabled or removed
   *  member still shows on the card without any roster. */
  assigneeName: string | null
  /** Whether the assignee can still be picked. Null when unassigned; false
   *  for a disabled person or a system account. Membership is not asked. */
  assigneeActive: boolean | null
  order: number
  createdBy: string
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  /** The item's own logged time — not its children's. */
  minutesLogged?: number
  /** Only on the company-wide list, where a key alone does not say where the
   *  task lives. */
  projectName?: string
  projectKeyPrefix?: string | null
  /** The epic a task sits under, or the task a sub-task sits under. Both in
   *  the same project. */
  parentTaskId: string | null
  /** The parent's key, when the list endpoint joins it in. */
  parentKey?: string | null
  /** The sprint the task is in now — at most one. */
  sprintId: string | null
  sprintName?: string | null
  /** The epic this item belongs to: a task's parent, a sub-task's
   *  grandparent. Null for an epic, a lone task, or a lone task's sub-task. */
  epicId?: string | null
  epicKey?: string | null
  /** Direct children, non-archived, and how many are in a done-category
   *  status: an epic counts its tasks, a task its sub-tasks. List endpoints. */
  childTotal?: number
  childDone?: number
  /**
   * Paths as the agent reached them from where its session started, e.g.
   * "orders/src/Order.java". The leading folder is what says which system a
   * change is in when a task spans more than one. Mostly on sub-tasks.
   */
  filesToModify?: string[]
  implementationNotes?: string | null
  /** Single-task read only. */
  children?: Task[]
  parent?: TaskRef | null
  /** A sub-task's epic (its grandparent). Single-task read only. */
  epic?: TaskRef | null
  /** Children still open — what archiving the parent leaves behind. */
  openChildren?: number
  /** Expected effort, set before the work starts. Whole minutes; null means
   *  not estimated, which is not the same as zero. */
  estimateMinutes: number | null
  /** Single-task read only: the estimates of the whole subtree below the
   *  item, archived excluded. */
  childEstimateMinutes?: number
}

/** GET /projects/:id/search — one page of an issue search. */
export type IssueSearchResult = {
  items: (Task & {
    rank: number
    /** Where the query's words were found. Empty when the query has no text. */
    matchedIn: ("key" | "title" | "description" | "comment")[]
    /** An excerpt with each match between <mark> and </mark>: from the
     *  description, or from the best-matching comment when only one matched. */
    snippet: string | null
    /** The comment the snippet came from. */
    commentId: string | null
  })[]
  /** Every match, not just this page. */
  total: number
  /** What the query box underlines: unknown fields, values not on this board. */
  warnings: { message: string; start: number; end: number }[]
  sort: { key: "relevance" | "updated" | "created" | "priority" | "status" | "key" | "due"; dir: "asc" | "desc" }[]
}

/** GET /projects/:id/search/suggest — what fits at the caret. */
export type IssueSearchSuggestions = {
  /** The span of the query a suggestion replaces. */
  from: number
  to: number
  options: { label: string; insert: string; description: string }[]
}

export type SprintStatus = "planned" | "active" | "closed"

export type Sprint = {
  id: string
  projectId: string
  name: string
  goal: string | null
  startDate: string | null
  endDate: string | null
  /** Set by hand, not derived from the dates. At most one active per project. */
  status: SprintStatus
  order: number
  closedAt: string | null
  createdAt: string
  updatedAt: string
  taskCount?: number
}

/** What GET /tasks/:id/time answers. `children` are the direct children; each
 *  child's minutes cover its own time and its sub-tasks'. */
export type TimeLog = {
  entries: TimeEntry[]
  totalMinutes: number
  childMinutes?: number
  children?: {
    taskId: string
    key: string
    title: string
    level?: TaskLevel
    minutes: number
    estimateMinutes?: number | null
  }[]
}

export type TimeEntry = {
  id: string
  taskId: string
  employeeId: string
  employeeName: string
  /** The admin who logged it for the employee; null when they logged it themselves. */
  loggedById: string | null
  loggedByName: string | null
  minutes: number
  /** The day the work happened, which is not the day it was typed in. */
  spentOn: string
  note: string | null
  createdAt: string
}

// `url` is a pre-signed link straight to the bucket, good for an hour. The
// API route it replaces is Bearer-authenticated, and a plain <img src> or
// <a href> carries no header — so the URL has to come with the listing. It is
// null when the server has no SPACES_* configured.
export type CommentAttachment = {
  id: string
  commentId: string
  filename: string
  mimeType: string
  sizeBytes: number
  /** Whether the browser may show it in place. Everything else downloads:
   *  the signed URL itself forces it, so a link cannot open an HTML or SVG. */
  inline: boolean
  url: string | null
  createdAt: string
}

export type Comment = {
  id: string
  taskId: string
  body: string
  source: "human" | "agent"
  authorId: string
  authorName: string
  createdAt: string
  updatedAt: string
  attachments?: CommentAttachment[]
}

// A space is also the source its pages came from — kind says which, and
// credentialHint says whether a credential is stored. The credential itself is
// never sent: see PUBLIC_COLUMNS in cloud's src/context-hub/spaces.ts.
export type SpaceKind = "manual" | "confluence" | "bookstack" | "google-drive"

export type Space = {
  id: string
  name: string
  description: string | null
  kind: SpaceKind
  settings: Record<string, unknown>
  credentialHint: string | null
  /** Days before a page here should be looked at again. Null = never expires. */
  reviewAfterDays: number | null
  lastImportedAt: string | null
  lastImportStatus: "ok" | "partial" | "failed" | null
  lastImportMessage: string | null
  archivedAt: string | null
  createdAt: string
  updatedAt: string
}

/** What POST /context-hub/spaces/:id/import returns. Counts rather than a list:
 *  a sync of a real wiki touches hundreds of pages. */
export type ImportResult = {
  pagesCreated: number
  pagesUpdated: number
  /** Kept as-is because someone edited them here after the last sync. */
  pagesSkipped: number
  pagesUnchanged: number
  /** Refused because the upstream content carried markup that can execute. */
  pagesRefused: number
  /** Files of a type we do not read — images, archives. Not a problem, so it
   *  never makes a sync "partial". Always 0 for Confluence and BookStack. */
  filesUnsupported: number
  /** Images copied from BookStack into our storage this sync. */
  imagesImported: number
  /** Images that could not be copied; the page keeps a link to the original. */
  imagesFailed: number
  errors: string[]
  /** Pages of the space this sync left as they were, and why. Capped; the
   *  rest are counted in notReimportedOmitted. Absent from older results. */
  notReimported?: NotReimported[]
  notReimportedOmitted?: number
}

export type NotReimported = {
  id: string
  title: string
  path: string
  reason: "edited" | "written-here" | "gone-upstream" | "refused"
}

/** GET /context-hub/stats — coverage and health of the wiki. */
export type HubStats = {
  pages: number
  embedded: number
  failed: number
  blocked: number
  /** Edited since they were embedded, so semantic search is answering from old text. */
  drifted: number
  stale: number
  chunks: number
  /** Pages whose vectors came from a retired embedding model — invisible to
   *  semantic search until re-embedded. */
  outdatedModel: number
}

/** POST /context-hub/spaces/:id/embed */
export type EmbedOutcome = {
  pagesEmbedded: number
  pagesSkipped: number
  pagesFailed: number
  chunksWritten: number
  chunksReused: number
  errors: string[]
}

export type SearchResult = {
  pageId: string
  spaceId: string
  spaceName: string
  path: string
  title: string
  headingPath: string[]
  snippet: string
  score: number
  keywordScore: number
  semanticScore: number
  stale: boolean
  updatedAt: string
}

/** An internal link on a page, resolved by the server to the page it reaches.
 *  A null targetId is a broken link. */
export type ResolvedLink = {
  href: string
  targetPath: string
  targetId: string | null
}

export type Page = {
  id: string
  spaceId: string
  /** The hierarchy is the path: "sap/commerce/checkout.md". No folder entity. */
  path: string
  title: string
  content?: string
  format?: string
  type: string
  tags: string[]
  /** The page's id upstream, when it was imported. Null for authored pages. */
  sourceRef: string | null
  /** null (never) | embedding | embedded | failed | blocked */
  embedStatus: string | null
  expiresAt: string | null
  /** Soft delete. The page stays reachable by id so citations and inbound
   *  links do not dangle. */
  deletedAt?: string | null
  updatedAt: string
  createdAt: string
}

/** One row of GET /analytics, whatever it was grouped by. */
export type AnalyticsRow = {
  key: string | null
  label: string
  inputTokens: number
  outputTokens: number
  totalTokens: number
  chargedMicroUsd: number
  chargedFormatted: string
  sessions: number
  requests: number
}

export type AnalyticsReport = {
  from: string
  to: string
  groupBy: string
  totals: {
    totalTokens: number
    chargedMicroUsd: number
    chargedFormatted: string
    requests: number
  }
  rows: AnalyticsRow[]
}

export type DailyAnalyticsSplit = "employee" | "project" | "task" | "model" | "kind"

export type DailyAnalyticsReport = {
  from: string
  to: string
  splitBy: DailyAnalyticsSplit
  totals: {
    chargedMicroUsd: number
    chargedFormatted: string
    totalTokens: number
    requests: number
  }
  series: {
    key: string | null
    label: string
    chargedMicroUsd: number
    chargedFormatted: string
  }[]
  days: {
    day: string
    chargedMicroUsd: number
    chargedFormatted: string
    totalTokens: number
    requests: number
    segments: {
      key: string | null
      label: string
      chargedMicroUsd: number
      chargedFormatted: string
      totalTokens: number
      requests: number
    }[]
  }[]
}

/** The staff view. Same shape as AnalyticsReport plus the cost basis, which the
 *  company-facing endpoint never returns. */
export type PlatformReport = {
  from: string
  to: string
  groupBy: string
  totals: AnalyticsReport["totals"] & { costMicroUsd: number; marginMicroUsd: number }
  rows: (AnalyticsRow & { costMicroUsd: number; marginMicroUsd: number })[]
}

export type Usage = {
  totalInput: number
  totalOutput: number
  totalReasoning: number
  totalCacheRead: number
  totalCacheWrite: number
  chargedMicroUsd: number
  chargedFormatted?: string
  records: number
  sessions?: number
  activeEmployees?: number
}

// Customer-facing endpoints report what the company was *charged*. Our cost
// basis is `cost*` and appears only under /platform, which this app never
// calls — typing these stops the two being confused at a call site.
export type EmployeeUsage = {
  employeeId: string
  employeeName: string
  employeeEmail: string
  totalInput: number
  totalOutput: number
  chargedMicroUsd: number
  chargedFormatted: string
  sessions: number
}

export type DailyUsage = {
  date: string
  inputTokens: number
  outputTokens: number
  chargedMicroUsd: number
  sessions: number
}

export type Invitation = {
  id: string
  email: string
  role: string
  expiresAt: string
  acceptedAt: string | null
  createdAt: string
}

export type Purchase = {
  id: string
  amountCents: number
  creditedMicroUsd: number
  status: "pending" | "paid" | "failed"
  /** purchase | grant — a grant is credit Moderniser staff gave. */
  kind: "purchase" | "grant"
  note: string | null
  createdAt: string
}

/** GET /auth/me/companies — what the company switcher and picker offer. */
export type MyCompany = {
  id: string
  name: string
  domain?: string
  role: string
}

// ---------- staff-only shapes (/platform) ----------

export type PlatformCompany = {
  id: string
  name: string
  domain: string
  creditsMicroUsd: number
  creditsFormatted: string
  marginBps: number
  marginPercent: string
  suspendedAt: string | null
  billingEnforced: boolean
  costMicroUsd: number
  chargedMicroUsd: number
  grossProfitMicroUsd: number
  grossProfitFormatted: string
  records: number
  createdAt: string
}

export type PlatformCompanyDetail = {
  company: PlatformCompany & { featureFlags: Record<string, boolean> }
  totals: {
    costMicroUsd: number
    costFormatted: string
    chargedMicroUsd: number
    chargedFormatted: string
    grossProfitMicroUsd: number
    grossProfitFormatted: string
    records: number
  }
}

/** A person's place in one company: their role there, how much of that
 *  company's credits they may spend, and whether the company has switched
 *  their access to it off. */
export type PlatformMembership = {
  companyId: string
  companyName: string
  role: string
  status: "active" | "disabled"
  spendCap24hMicroUsd: number | null
  spendCap7dMicroUsd: number | null
  spendCap30dMicroUsd: number | null
}

export type PlatformEmployee = {
  id: string
  name: string
  email: string
  /** platform_admin for staff, "member" for everyone else — company roles are per membership. */
  role: string
  status: string
  sessionsRevokedAt: string | null
  createdAt: string
  memberships: PlatformMembership[]
}

// GET /workspace-sessions/:id/usage — what one chat session has cost.
//
// Cost cannot come from the agent: the v2 session runner publishes a hardcoded
// `cost: 0` on every step, so `session.cost` and every message's `cost` are
// permanently zero on the path this app uses. This is the ledger's figure, and
// the only one with the company's margin applied.
export type SessionUsage = {
  usage: {
    totalInput: number
    totalOutput: number
    totalReasoning: number
    totalCacheRead: number
    totalCacheWrite: number
    chargedMicroUsd: number
    records: number
  }
  /** Formatted by cloud, whose precision scales with magnitude — a single turn
   *  is a fraction of a cent and would render as "$0.00" at two decimals. */
  chargedFormatted: string
  /** The company this session's inference is billed to — the workspace's,
   *  never the viewer's home company. */
  billedTo: { id: string; name: string }
}

export type PlatformModel = {
  modelKey: string
  kind: "chat" | "embedding"
  provider: string
  apiModel: string
  displayName: string
  enabled: boolean
  isDefault: boolean
  defaultMaxTokens: number
  // The whole context window, prompt and completion together — what the chat's
  // status bar divides by. 0 means nobody has looked it up.
  contextWindow: number
  dimensions: number | null
  inputUsdPerMtok: number
  outputUsdPerMtok: number
  cachedInputUsdPerMtok: number
  cacheWrite5mUsdPerMtok: number
  cacheWrite1hUsdPerMtok: number
  notes: string | null
  updatedAt: string
}

export type PlatformRates = {
  marginBps: number
  marginPercent: string
  unit: string
  models: {
    modelKey: string
    displayName: string
    kind: string
    cost: { input: number; output: number; cachedInput: number }
    charged: { input: number; output: number; cachedInput: number }
  }[]
}

export type AdminActionEntry = {
  id: string
  action: string
  targetType: string
  targetId: string | null
  companyId: string | null
  payload: Record<string, unknown>
  createdAt: string
  actorName: string | null
  actorEmail: string | null
}

export type ApiToken = {
  id: string
  tokenPrefix: string
  label: string
  active: boolean
  lastUsedAt: string | null
  createdAt: string
}

// ---------- migrations ----------
// Importing a whole project from another tracker. One run per migration; the
// server does the work in the background and the wizard polls the run.

export type MigrationSourceId = "youtrack" | "jira" | "linear"

export type MigrationSource = {
  id: MigrationSourceId
  name: string
  available: boolean
  description: string
  connectFields: { name: "url" | "token"; label: string; secret?: boolean; placeholder?: string; hint: string }[]
  /** One step per item; may contain **bold**. */
  tokenHelp: string[]
}

export type SourceProject = { id: string; key: string; name: string; issueCount: number }

export type MigrationCheck = {
  account: string
  projects: SourceProject[]
  canReadEmails: boolean
  canReadBoards: boolean
  warnings: string[]
}

/** Where the issues land. keepSourceNumbers keys each task by its source
 *  number (CAD-176 → <prefix>-176); the server defaults it to true for a new
 *  or empty project and refuses it into one that already has tasks. */
export type MigrationTarget =
  | { projectId: string; keepSourceNumbers?: boolean }
  | { new: { name: string; keyPrefix: string }; keepSourceNumbers?: boolean }

export type MigrationStatus =
  | "surveying"
  | "ready"
  | "previewing"
  | "previewed"
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"

export type MigrationPhase = "reading" | "downloading" | "writing" | "uploading"

export type BoardChoice = { id: string; name: string; category?: "todo" | "in_progress" | "done" }

export type MigrationSuggestions = {
  board: { statuses: BoardChoice[]; types: BoardChoice[] }
  priorities: BoardChoice[]
  states: { value: string; uses: number }[]
  types: { value: string; uses: number }[]
  priorityValues: { value: string; uses: number }[]
  people: { login: string; name: string; email: string | null; kind: "existing" | "new" | "system" }[]
  estimationFields: string[]
  boards: { id: string; name: string; sprints: number }[]
  restricted: { issues: number; comments: number }
  /** Link types besides Subtask that carry epics and stories, with link counts. */
  hierarchyLinks: { name: string; role: "parentOf" | "childOf"; links: number }[]
  /** Another project that already holds this source's tasks; importing
   *  anywhere else is refused. */
  priorImport: { projectId: string; key: string; name: string; tasks: number } | null
}

/** "__create" adds a status or type with the value's own name to the board. */
export type MappingDraft = {
  status: Record<string, string | null>
  type: Record<string, string>
  priority: Record<string, string>
  estimationField: string | null
  sprintBoard: string | null
  includeRestricted: boolean
  /** Names from suggestions.hierarchyLinks to read the hierarchy from. */
  hierarchyLinks: string[]
}

export type MigrationCounts = {
  issues: number
  /** Every imported item; epics + tasks + subtasks = items. Each has a key. */
  items: number
  epics: number
  tasks: number
  subtasks: number
  comments: number
  mentions: number
  attachments: number
  attachmentBytes: number
  timeEntries: number
  timeMinutes: number
  sprints: number
  tasksInSprint: number
  statusEvents: number
  newPeople: number
  systemAccounts: number
  skipped: number
}

export type MigrationSkipped = { what: string; ref: string; reason: string }

export type MigrationPreview = {
  counts: MigrationCounts
  reconciled: boolean
  storage: { usedBytes: number; addBytes: number; limitBytes: number }
  warnings: string[]
  problems: string[]
  skipped: MigrationSkipped[]
}

export type MigrationSummary = {
  counts: MigrationCounts
  warnings: string[]
  skipped: MigrationSkipped[]
  newPeople: { name: string; email: string }[]
}

export type MigrationRun = {
  id: string
  source: MigrationSourceId
  status: MigrationStatus
  sourceProject: SourceProject
  target: MigrationTarget
  targetProject: { id: string; key: string; name: string } | null
  phase: MigrationPhase | null
  progress: { done: number; total: number; label: string } | null
  error: string | null
  credentialHint: string | null
  suggestions: MigrationSuggestions | null
  mapping: MappingDraft | null
  preview: MigrationPreview | null
  summary: MigrationSummary | null
  createdByName: string
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
}
